import {
  effectiveSplit,
  SERVICE_FEE_RATE,
  SHIPPING_COST,
  type SplitSource,
} from "./order-domain";

export const STATS_RANGES = {
  "7": { label: "近 7 天", compare: "前 7 天", days: 7, grouping: "day" },
  "30": { label: "近 30 天", compare: "前 30 天", days: 30, grouping: "day" },
  "60": { label: "近 60 天", compare: "前 60 天", days: 60, grouping: "day" },
  "180": { label: "近 180 天", compare: "前 180 天", days: 180, grouping: "week" },
  all: { label: "全部时间", compare: "", days: null, grouping: "month" },
} as const;

export type StatsRangeKey = keyof typeof STATS_RANGES;
export type StatsGrouping = (typeof STATS_RANGES)[StatsRangeKey]["grouping"];

export interface StatsOrder extends SplitSource {
  order_time: string | null;
  total_price: number | null;
  items?: Array<{ color: string | null; quantity: number }>;
}

export interface StatsSummary {
  revenue: number;
  orderCount: number;
  periodDays: number;
  dailyRevenue: number;
  activeDays: number;
  totalSplit: number;
  serviceFee: number;
  shippingCost: number;
  earned: number;
}

export function statsToday(now = new Date()): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

export function statsRangeBounds(
  rangeKey: Exclude<StatsRangeKey, "all">,
  now = new Date(),
  periodOffset = 0,
): { start: Date; end: Date } {
  const days = STATS_RANGES[rangeKey].days;
  const end = statsToday(now);
  end.setDate(end.getDate() + 1 - days * periodOffset);
  const start = new Date(end);
  start.setDate(start.getDate() - days);
  return { start, end };
}

export function orderStatsDate(order: Pick<StatsOrder, "order_time">): Date | null {
  if (!order.order_time) return null;
  const date = new Date(order.order_time);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function getOrdersInRange<T extends StatsOrder>(
  orders: readonly T[],
  rangeKey: StatsRangeKey,
  now = new Date(),
  periodOffset = 0,
): T[] {
  if (rangeKey === "all") return periodOffset === 0 ? [...orders] : [];
  const { start, end } = statsRangeBounds(rangeKey, now, periodOffset);
  return orders.filter((order) => {
    const date = orderStatsDate(order);
    return Boolean(date && date >= start && date < end);
  });
}

export function statsDateKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

export function statsPeriodDayCount(
  orders: readonly StatsOrder[],
  rangeKey: StatsRangeKey,
  now = new Date(),
): number {
  const days = STATS_RANGES[rangeKey].days;
  if (days) return days;
  const dates = orders
    .map(orderStatsDate)
    .filter((date): date is Date => Boolean(date))
    .sort((left, right) => left.getTime() - right.getTime());
  if (dates.length === 0) return 1;
  const first = new Date(dates[0].getFullYear(), dates[0].getMonth(), dates[0].getDate());
  return Math.max(1, Math.floor((statsToday(now).getTime() - first.getTime()) / 86_400_000) + 1);
}

export function sumOrderRevenue(orders: readonly StatsOrder[]): number {
  return orders.reduce((sum, order) => sum + (order.total_price ?? 0), 0);
}

export function statsDelta(current: number, previous: number): number | null {
  return previous > 0 ? ((current - previous) / previous) * 100 : null;
}

export function buildStatsSummary(
  allOrders: readonly StatsOrder[],
  rangeKey: StatsRangeKey,
  now = new Date(),
): StatsSummary {
  const orders = getOrdersInRange(allOrders, rangeKey, now);
  const revenue = sumOrderRevenue(orders);
  const orderCount = orders.length;
  const periodDays = statsPeriodDayCount(orders, rangeKey, now);
  const totalSplit = orders.reduce((sum, order) => sum + effectiveSplit(order), 0);
  const serviceFee = revenue * SERVICE_FEE_RATE;
  const shippingCost = orderCount * SHIPPING_COST;
  const activeDays = new Set(
    orders.map(orderStatsDate).filter((date): date is Date => Boolean(date)).map(statsDateKey),
  ).size;
  return {
    revenue,
    orderCount,
    periodDays,
    dailyRevenue: revenue / periodDays,
    activeDays,
    totalSplit,
    serviceFee,
    shippingCost,
    earned: revenue - serviceFee - shippingCost - totalSplit,
  };
}
